import {
  AGENCY_BROADCAST_SUBJECT,
  renderAgencyBroadcast,
  type BroadcastShiftDay,
} from "./agencyBroadcast";
import { audienceLabel } from "./catalog";
import { listAutomations } from "./storage";
import type {
  DelayConfig,
  HospitalAutomation,
  NotifyAgenciesConfig,
  ReleaseAudience,
  ReleaseStage,
  ReleaseStagesConfig,
} from "./types";

const POST_KIND_TO_AUDIENCE: Record<string, ReleaseAudience> = {
  "decide.post_internal": "internal",
  "decide.post_precredential": "precredential",
  "decide.post_favorite": "favorite",
  "decide.post_worked_before": "worked_before",
  "decide.post_public": "public",
};

export function isPostShiftAutomation(automation: HospitalAutomation) {
  return automation.nodes.some((node) => node.kind === "listen.shift_posted");
}

export function listPostShiftAutomations(
  hospitalId: string,
  enabledOnly = false,
) {
  return listAutomations(hospitalId)
    .filter((item) => isPostShiftAutomation(item))
    .filter((item) => (enabledOnly ? item.enabled : true))
    .sort((left, right) => Number(right.enabled) - Number(left.enabled));
}

function walkPostPlan(automation: HospitalAutomation): ReleaseStage[] {
  const start = automation.nodes.find((node) => node.kind === "listen.shift_posted");
  if (!start) return [];
  const outgoing = new Map<string, string[]>();
  automation.edges.forEach((edge) => {
    const list = outgoing.get(edge.source) || [];
    list.push(edge.target);
    outgoing.set(edge.source, list);
  });
  const stages: ReleaseStage[] = [];
  const seen = new Set<string>();
  const visit = (id: string, pendingDays: number) => {
    if (seen.has(id)) return;
    seen.add(id);
    const node = automation.nodes.find((item) => item.id === id);
    if (!node) return;
    let nextDays = pendingDays;
    if (node.kind === "collect.delay") {
      nextDays += Math.max(0, Number((node.config as DelayConfig).days) || 0);
    }
    const audience = POST_KIND_TO_AUDIENCE[node.kind];
    if (audience) {
      stages.push({ audience, days: nextDays });
      nextDays = 0;
    }
    (outgoing.get(id) || []).forEach((target) => visit(target, nextDays));
  };
  visit(start.id, 0);
  return stages;
}

export function releaseStagesFromAutomation(
  automation: HospitalAutomation | null | undefined,
): ReleaseStage[] {
  if (!automation) return [];
  const walked = walkPostPlan(automation);
  if (walked.length) return walked;
  const legacy = automation.nodes.find((item) => item.kind === "decide.release_stages");
  const stages = (legacy?.config as ReleaseStagesConfig | undefined)?.stages;
  return Array.isArray(stages) ? stages : [];
}

export function summarizeReleasePlan(stages: ReleaseStage[]) {
  if (!stages.length) return "Add post actions and wait days on the board";
  return stages
    .map((stage, index) => {
      const label = `post ${audienceLabel(stage.audience).toLowerCase()}`;
      if (index === 0 && !stage.days) return label;
      if (stage.days) return `wait ${stage.days}d, then ${label}`;
      return label;
    })
    .join(" → ");
}

export function stagesToPremiere(stages: ReleaseStage[]) {
  const exclusive = stages.filter((stage) => stage.audience !== "public");
  const audiences = exclusive.flatMap((stage) => {
    if (stage.audience === "internal") return ["INTERNAL"];
    if (stage.audience === "precredential") return ["PRE_CREDENTIALED"];
    if (stage.audience === "favorite") return ["FAVORITE"];
    if (stage.audience === "worked_before") return ["WORKED_BEFORE"];
    return [];
  });
  const days = exclusive.reduce((sum, stage) => sum + (Number(stage.days) || 0), 0);
  if (!audiences.length) return {};
  return {
    premiereConfig: {
      enabled: true,
      audiences,
      days: Math.max(1, days),
    },
  };
}

export function agencyBroadcastFromAutomation(
  automation: HospitalAutomation | null | undefined,
) {
  const node = automation?.nodes.find((item) => item.kind === "notify.agencies");
  if (!node) return null;
  const config = node.config as NotifyAgenciesConfig;
  const emails = (config.emails || []).filter((email) => email.includes("@"));
  if (!emails.length) return null;
  return {
    emails,
    subject: config.subject || AGENCY_BROADCAST_SUBJECT,
    body: config.body || "",
  };
}

export function postingAutomationPayload(
  automation: HospitalAutomation,
  context?: {
    grouped?: boolean;
    specialty?: string;
    skillLevel?: string;
    days?: BroadcastShiftDay[];
  },
) {
  const stages = releaseStagesFromAutomation(automation);
  const agencies = agencyBroadcastFromAutomation(automation);
  const rendered =
    agencies && context
      ? renderAgencyBroadcast({
          subject: agencies.subject,
          body: agencies.body,
          grouped: !!context.grouped,
          specialty: context.specialty,
          skillLevel: context.skillLevel,
          days: context.days || [],
        })
      : null;
  return {
    ...stagesToPremiere(stages),
    automationConfig: {
      automationId: automation.id,
      name: automation.name,
      stages,
      agencies: agencies
        ? {
            ...agencies,
            ...(rendered
              ? {
                  subject: rendered.subject,
                  body: rendered.body,
                  openDates: rendered.open,
                }
              : {}),
          }
        : null,
      runPolicy: {
        stopSoloWhenConfirmed: true,
        continueGroupWhileFutureOpen: true,
      },
    },
  };
}
